import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  UserCog, Plus, Pencil, Trash2, KeyRound, ShieldOff, ShieldCheck, Power,
  BriefcaseBusiness, Lock, CalendarClock,
} from "lucide-react";
import {
  MODULES, MODULE_LABEL, DESIGNATIONS, MEMBERSHIP_BODIES, DESIGNATION_PRESETS,
  MEMBERSHIP_FOR_DESIGNATION, engagementStatus,
} from "@/lib/access";
import { fmtDate } from "@/lib/format";

const primaryBtn = "bg-[color:var(--mk-primary)] text-[#0B1220] hover:bg-[color:var(--mk-primary)]/90 font-medium";
const surface = "border-[color:var(--mk-border)] bg-[color:var(--mk-surface)]";
const dialogSurface = "border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)]";

const STATUS_BADGE = {
  active: "border-[rgba(15,61,46,0.55)] bg-[rgba(15,61,46,0.35)] text-[color:var(--mk-text)]",
  expired: "border-[rgba(74,31,42,0.55)] bg-[rgba(74,31,42,0.35)] text-[color:var(--mk-text)]",
  pending: "border-[rgba(176,141,87,0.35)] bg-[rgba(176,141,87,0.18)] text-[color:var(--mk-text)]",
  inactive: "border-[color:var(--mk-border-strong)] text-[color:var(--mk-muted)]",
};

const emptyForm = () => ({
  name: "", email: "", designation: "Chartered Accountant (CA)", firm_name: "",
  membership_body: "ICAI", membership_no: "", phone: "",
  scope_all: true, entity_ids: [], module_access: { ...DESIGNATION_PRESETS["Chartered Accountant (CA)"] },
  valid_from: "", valid_until: "",
  eng_firm: "", eng_letter_ref: "", eng_start: "", eng_end: "",
});

function downloadKeyFile(tokenText, filename) {
  const blob = new Blob([tokenText], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "advisor.fokey";
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function Professionals() {
  const { user } = useAuth();
  const [pros, setPros] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, e] = await Promise.all([
        api.get("/professionals"),
        api.get("/entities").catch(() => ({ data: [] })),
      ]);
      setPros(p.data || []); setEntities(e.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load professionals");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setModule = (key, level) => setForm((f) => {
    const ma = { ...f.module_access };
    if (level === "none") delete ma[key]; else ma[key] = level;
    return { ...f, module_access: ma };
  });
  const applyPreset = (designation) => {
    const preset = DESIGNATION_PRESETS[designation] || {};
    setForm((f) => ({
      ...f, designation,
      membership_body: MEMBERSHIP_FOR_DESIGNATION[designation] || f.membership_body,
      module_access: { ...preset },
    }));
  };

  const openDialog = (p) => {
    if (p) {
      setEdit(p);
      const scopeAll = p.entity_scope === "all" || !Array.isArray(p.entity_scope);
      setForm({
        name: p.name || "", email: p.email || "", designation: p.designation || DESIGNATIONS[0],
        firm_name: p.firm_name || "", membership_body: p.membership_body || "ICAI",
        membership_no: p.membership_no || "", phone: p.phone || "",
        scope_all: scopeAll, entity_ids: scopeAll ? [] : p.entity_scope,
        module_access: { ...(p.module_access || {}) },
        valid_from: p.valid_from ? String(p.valid_from).slice(0, 10) : "",
        valid_until: p.valid_until ? String(p.valid_until).slice(0, 10) : "",
        eng_firm: p.engagement?.firm || "", eng_letter_ref: p.engagement?.letter_ref || "",
        eng_start: p.engagement?.start_date ? String(p.engagement.start_date).slice(0, 10) : "",
        eng_end: p.engagement?.end_date ? String(p.engagement.end_date).slice(0, 10) : "",
      });
    } else { setEdit(null); setForm(emptyForm()); }
    setOpen(true);
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    email: form.email.trim().toLowerCase(),
    designation: form.designation,
    firm_name: form.firm_name || null,
    membership_body: form.membership_body || null,
    membership_no: form.membership_no || null,
    phone: form.phone || null,
    entity_scope: form.scope_all ? "all" : form.entity_ids,
    module_access: form.module_access,
    valid_from: form.valid_from || null,
    valid_until: form.valid_until || null,
    engagement: {
      firm: form.eng_firm || form.firm_name || null,
      letter_ref: form.eng_letter_ref || null,
      start_date: form.eng_start || null,
      end_date: form.eng_end || null,
    },
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!edit && !/\S+@\S+\.\S+/.test(form.email)) { toast.error("A valid email is required"); return; }
    if (Object.keys(form.module_access).length === 0) { toast.error("Grant at least one module of access"); return; }
    if (!form.scope_all && form.entity_ids.length === 0) { toast.error("Pick at least one entity, or choose All entities"); return; }
    setBusy(true);
    try {
      if (edit) {
        await api.put(`/professionals/${edit.id}`, buildPayload());
        toast.success("Professional updated");
      } else {
        const { data } = await api.post("/professionals", buildPayload());
        toast.success("Professional onboarded");
        // immediately offer their key file
        await provisionKey(data.id, data.name, true);
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const provisionKey = async (id, name, silent) => {
    try {
      const { data } = await api.post(`/professionals/${id}/provision-token`);
      downloadKeyFile(data.token, data.filename);
      toast.success(`Key file generated for ${name}. Hand it over securely.`);
    } catch (e) { if (!silent) toast.error("Could not generate key file"); }
  };

  const revoke = async (p) => {
    try { await api.post(`/professionals/${p.id}/revoke`); toast.success("Key revoked — existing files are now invalid"); load(); }
    catch { toast.error("Could not revoke"); }
  };
  const toggleActive = async (p) => {
    try { await api.post(`/professionals/${p.id}/set-active`, { active: !p.active }); toast.success(p.active ? "Deactivated" : "Activated"); load(); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (p) => {
    try { await api.delete(`/professionals/${p.id}`); toast.success("Professional removed"); load(); }
    catch { toast.error("Could not delete"); }
  };

  if (!isAdmin) {
    return (
      <div className="animate-fade-up">
        <PageHeader title="Professionals & Advisors" />
        <Card className={surface}><EmptyState icon={Lock} title="Admins only" description="Only family office administrators can onboard and manage the professional fleet." /></Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      <PageHeader title="Professionals & Advisors" subtitle="Onboard CAs, CMAs, CS, auditors and book-keepers with precise, time-bound access">
        <Button onClick={() => openDialog(null)} className={primaryBtn} data-testid="onboard-professional-button"><Plus className="mr-2 h-4 w-4" /> Onboard Professional</Button>
      </PageHeader>

      {loading ? (
        <Skeleton className="h-72 rounded-[18px]" />
      ) : pros.length === 0 ? (
        <Card className={surface}><EmptyState icon={BriefcaseBusiness} title="No professionals onboarded yet" description="Add your chartered accountants, company secretaries, auditors and book-keepers. Each gets a scoped, time-bound encrypted key." action={<Button onClick={() => openDialog(null)} className={primaryBtn}>Onboard Professional</Button>} /></Card>
      ) : (
        <Card className={`${surface} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="professionals-table">
              <thead className="bg-[rgba(16,27,43,0.6)]">
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--mk-text-2)]">
                  <th className="px-4 py-3">Name</th><th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Access</th><th className="px-4 py-3">Validity</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pros.map((p) => {
                  const status = engagementStatus(p);
                  const modCount = Object.keys(p.module_access || {}).length;
                  return (
                    <tr key={p.id} className="border-t border-[color:var(--mk-border)] hover:bg-[rgba(176,141,87,0.06)]" data-testid={`professional-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[color:var(--mk-text)]">{p.name}</div>
                        <div className="text-[11px] text-[color:var(--mk-muted)]">{p.email}{p.firm_name ? ` · ${p.firm_name}` : ""}</div>
                      </td>
                      <td className="px-4 py-3 text-[color:var(--mk-text-2)]">
                        {p.designation}
                        {p.membership_no ? <div className="text-[11px] text-[color:var(--mk-muted)]">{p.membership_body} · {p.membership_no}</div> : null}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline" className="border-[color:var(--mk-border-strong)] text-[color:var(--mk-text-2)]">{modCount} module{modCount === 1 ? "" : "s"}</Badge>
                          <Badge variant="outline" className="border-[color:var(--mk-border-strong)] text-[color:var(--mk-text-2)]">{p.entity_scope === "all" || !Array.isArray(p.entity_scope) ? "All entities" : `${p.entity_scope.length} entit${p.entity_scope.length === 1 ? "y" : "ies"}`}</Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[color:var(--mk-text-2)]">
                        <div className="flex items-center gap-1.5 text-xs"><CalendarClock className="h-3.5 w-3.5 text-[color:var(--mk-muted)]" />{p.valid_until ? `till ${fmtDate(p.valid_until)}` : "No expiry"}</div>
                      </td>
                      <td className="px-4 py-3"><Badge className={`capitalize ${STATUS_BADGE[status]}`} data-testid={`professional-status-${p.id}`}>{status}</Badge></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[color:var(--mk-primary-2)]" title="Generate key file" onClick={() => provisionKey(p.id, p.name)} data-testid={`provision-key-${p.id}`}><KeyRound className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[color:var(--mk-text-2)] hover:text-[color:var(--mk-text)]" title="Edit" onClick={() => openDialog(p)} data-testid={`edit-professional-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[color:var(--mk-text-2)] hover:text-[color:var(--mk-text)]" title={p.active ? "Deactivate" : "Activate"} onClick={() => toggleActive(p)} data-testid={`toggle-active-${p.id}`}><Power className="h-4 w-4" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#D98A8A] hover:text-[#D98A8A]" title="Revoke key" data-testid={`revoke-key-${p.id}`}><ShieldOff className="h-4 w-4" /></Button></AlertDialogTrigger>
                            <AlertDialogContent className={dialogSurface}><AlertDialogHeader><AlertDialogTitle>Revoke access key?</AlertDialogTitle><AlertDialogDescription>{p.name}'s existing key file(s) will stop working immediately. You can issue a new key afterwards.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => revoke(p)} className="bg-[#7A2E38] hover:bg-[#7A2E38]/90">Revoke</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                          </AlertDialog>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#D98A8A] hover:text-[#D98A8A]" title="Remove" data-testid={`delete-professional-${p.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                            <AlertDialogContent className={dialogSurface}><AlertDialogHeader><AlertDialogTitle>Remove professional?</AlertDialogTitle><AlertDialogDescription>This permanently removes {p.name} and invalidates their access.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(p)} className="bg-[#7A2E38] hover:bg-[#7A2E38]/90">Remove</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Onboard / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`max-h-[90vh] max-w-2xl overflow-y-auto ${dialogSurface}`}>
          <DialogHeader>
            <DialogTitle className="font-display">{edit ? "Edit Professional" : "Onboard Professional"}</DialogTitle>
            <DialogDescription>Define identity, the modules they may use, the entities they can see, and an engagement window.</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-1">
            {/* Identity */}
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Fld label="Full Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pro-name-input" /></Fld>
              <Fld label="Email"><Input type="email" value={form.email} disabled={!!edit} onChange={(e) => set("email", e.target.value)} data-testid="pro-email-input" /></Fld>
              <Fld label="Designation">
                <Select value={form.designation} onValueChange={applyPreset}>
                  <SelectTrigger data-testid="pro-designation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DESIGNATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Firm / Practice"><Input value={form.firm_name} onChange={(e) => set("firm_name", e.target.value)} /></Fld>
              <Fld label="Membership Body">
                <Select value={form.membership_body} onValueChange={(v) => set("membership_body", v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{MEMBERSHIP_BODIES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Membership No."><Input value={form.membership_no} onChange={(e) => set("membership_no", e.target.value)} className="font-data" /></Fld>
              <Fld label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Fld>
            </section>

            {/* Module access matrix */}
            <section>
              <SectionTitle icon={ShieldCheck}>Module access</SectionTitle>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {MODULES.map((m) => {
                  const lvl = form.module_access[m.key] || "none";
                  return (
                    <div key={m.key} className="flex items-center justify-between rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] px-3 py-2">
                      <span className="text-sm text-[color:var(--mk-text)]">{m.label}</span>
                      <div className="flex overflow-hidden rounded-md border border-[color:var(--mk-border)]" data-testid={`module-${m.key}`}>
                        {["none", "view", "edit"].map((opt) => (
                          <button key={opt} type="button" onClick={() => setModule(m.key, opt)}
                            data-testid={`module-${m.key}-${opt}`}
                            className={`px-2.5 py-1 text-[11px] capitalize transition-colors ${lvl === opt ? "bg-[color:var(--mk-primary)] text-[#0B1220] font-semibold" : "text-[color:var(--mk-text-2)] hover:bg-white/[0.04]"}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Entity scope */}
            <section>
              <SectionTitle icon={BriefcaseBusiness}>Entity scope</SectionTitle>
              <div className="flex items-center justify-between rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] px-3 py-2.5">
                <div><div className="text-sm font-medium text-[color:var(--mk-text)]">All entities</div><div className="text-[11px] text-[color:var(--mk-muted)]">Access every company & trust in the family office</div></div>
                <Switch checked={form.scope_all} onCheckedChange={(v) => set("scope_all", v)} data-testid="scope-all-switch" />
              </div>
              {!form.scope_all && (
                <ScrollArea className="mt-2 h-40 rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-2">
                  <div className="space-y-1">
                    {entities.length === 0 ? <p className="px-2 py-2 text-xs text-[color:var(--mk-muted)]">No entities to scope.</p> : entities.map((e) => {
                      const checked = form.entity_ids.includes(e.id);
                      return (
                        <label key={e.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-white/[0.04]">
                          <Checkbox checked={checked} onCheckedChange={(v) => set("entity_ids", v ? [...form.entity_ids, e.id] : form.entity_ids.filter((x) => x !== e.id))} data-testid={`scope-entity-${e.id}`} />
                          <span className="text-sm text-[color:var(--mk-text-2)]">{e.name} <span className="text-[11px] text-[color:var(--mk-muted)]">· {e.type}</span></span>
                        </label>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </section>

            {/* Validity + engagement */}
            <section>
              <SectionTitle icon={CalendarClock}>Engagement & validity</SectionTitle>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Fld label="Valid From"><Input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} data-testid="pro-valid-from" /></Fld>
                <Fld label="Valid Until (auto-expiry)"><Input type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} data-testid="pro-valid-until" /></Fld>
                <Fld label="Engagement Letter Ref"><Input value={form.eng_letter_ref} onChange={(e) => set("eng_letter_ref", e.target.value)} /></Fld>
                <Fld label="Engaging Firm"><Input value={form.eng_firm} onChange={(e) => set("eng_firm", e.target.value)} placeholder={form.firm_name || "Firm name"} /></Fld>
              </div>
            </section>
          </div>

          <DialogFooter>
            <Button variant="outline" className="border-[color:var(--mk-border)]" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className={primaryBtn} data-testid="save-professional-button">
              {busy ? "Saving…" : edit ? "Save changes" : "Onboard & generate key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.12em] text-[color:var(--mk-muted)]">{label}</Label>
      {children}
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="mb-2.5 flex items-center gap-2">
      <Icon className="h-4 w-4 text-[color:var(--mk-primary-2)]" strokeWidth={2.25} />
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--mk-muted)]">{children}</span>
    </div>
  );
}
